import { useEffect, useState } from "react";
import axios from "axios";
import { Search, ShieldCheck } from "lucide-react";
import { API_BASE_URL as API } from "../apiConfig";
import PublicBrandMark from "../components/public/PublicBrandMark";

export default function PublicBookingLookupPage() {
  const [form, setForm] = useState({ confirmation_code: "", full_name: "" });
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    if (code) {
      update("confirmation_code", code.toUpperCase());
    }
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Check your booking | Shonan International";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const lookup = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setBookings([]);
    try {
      const response = await axios.post(`${API}/public-booking/lookup`, form, { timeout: 15000 });
      const results = response.data.bookings || (response.data.booking ? [response.data.booking] : []);
      setBookings(Array.isArray(results) ? results.filter((item) => item && typeof item === "object") : []);
    } catch (err) {
      const status = err.response?.status;
      const code = err.code === "ECONNABORTED" ? "LOOKUP-TIMEOUT-001" : status === 422 ? "LOOKUP-INPUT-422" : status === 404 ? "LOOKUP-NOT-FOUND-404"
        : status === 429 ? "LOOKUP-RATE-429" : status >= 500 ? "LOOKUP-SERVER-500"
          : !err.response ? "LOOKUP-NETWORK-001" : "LOOKUP-REQUEST-001";
      const message = status === 422 ? "Please check your booking number and passenger name."
        : status === 404 ? "No matching booking found. Please check the booking number and passenger name."
          : status === 429 ? "Too many attempts. Please try again later."
            : "Unable to check your booking now. Please try again or contact support.";
      const diagnostic = window.__SHONAN_WEB_ERRORS__?.record(code, { status });
      setError(`${message} Error code: ${code}${diagnostic ? ` · Reference: ${diagnostic.reference}` : ""}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0d0c] text-[#f6f2e9]">
      <style>{`.lookup-input{width:100%;border-radius:8px;border:1px solid #68634e;background:#0b0d0c;padding:0.85rem;font-size:1rem;color:#f6f2e9}.lookup-input::placeholder{color:#96978e}.lookup-input:focus{outline:2px solid #d7bb7755;border-color:#d7bb77}`}</style>
      <header className="border-b border-[#d8b769]/20 bg-[#050505]">
        <div className="mx-auto flex h-[70px] max-w-3xl items-center justify-between px-5">
          <PublicBrandMark href="/book" />
          <a href="/book" className="rounded-full border border-[#d8b769]/35 px-4 py-2 text-sm font-semibold text-[#f3d98b]">New booking</a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <section className="rounded-lg border border-[#d7bb77]/25 bg-[#151815] p-6 shadow-xl">
          <ShieldCheck className="mb-3 h-8 w-8 text-[#d7bb77]" />
          <h1 className="text-3xl font-bold">Check your booking</h1>
          <p className="mt-2 text-sm leading-6 text-[#c2c2b8]">
            Enter your confirmation code and full name. Contact details are masked for privacy.
          </p>

          <form onSubmit={lookup} className="mt-6 grid gap-4">
            <label>
              <span className="mb-1.5 block text-sm font-semibold text-[#dedbd1]">Confirmation code</span>
              <input
                className="lookup-input font-mono uppercase"
                value={form.confirmation_code}
                onChange={(event) => update("confirmation_code", event.target.value)}
                placeholder="SIC-7KQ9P / CBH5EKS8"
              />
            </label>
            <label>
              <span className="mb-1.5 block text-sm font-semibold text-[#dedbd1]">Full name</span>
              <input
                className="lookup-input"
                value={form.full_name}
                onChange={(event) => update("full_name", event.target.value)}
                placeholder="Passenger full name"
              />
              <span className="mt-1.5 block text-xs text-[#aaa99e]">
                Use the passenger name from booking. First/last name order and spaces do not matter.
              </span>
            </label>
            <button disabled={loading} className="inline-flex items-center justify-center rounded-lg bg-[#d7bb77] px-4 py-3 font-semibold text-[#171913] hover:bg-[#e5cc90] disabled:opacity-60">
              <Search className="mr-2 h-4 w-4" />
              {loading ? "Checking..." : "Check booking"}
            </button>
          </form>

          {error && <p className="mt-4 rounded-lg border border-[#f0a5a5]/30 bg-[#351e20] p-3 text-sm font-semibold text-[#ffc4c4]">{error}</p>}
        </section>

        {bookings.length > 0 && (
          <p className="mt-6 font-semibold" role="status">Bookings found: {bookings.length}</p>
        )}
        {bookings.map((booking) => (
          <section key={booking.confirmation_code} className="mt-6 border-t border-[#d7bb77]/25 py-6" data-testid="booking-summary">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#d7bb77]">Booking summary</p>
            <h2 className="mt-1 text-2xl font-bold">{booking.confirmation_code}</h2>
            <div className="mt-5 grid gap-3 text-sm text-[#dedbd1] sm:grid-cols-2">
              <Info label="Status" value={booking.status} />
              <Info label="Service" value={booking.service_type} />
              <Info label="Date" value={booking.departure_date} />
              <Info label="Time" value={`${booking.pickup_time || ""} Japan Standard Time`} />
              <Info label="Route" value={`${booking.pickup_location || booking.pickup_area || ""} → ${booking.destination || booking.dropoff_area || ""}`} />
              <Info label="Vehicle" value={booking.vehicle_type} />
              <Info label="Passengers / luggage" value={`${booking.passenger_count || "-"} / ${booking.luggage_count || "-"}`} />
              <Info label="Total" value={booking.total_price ? `¥${Number(booking.total_price).toLocaleString()}` : "Pending quote"} />
              <Info label="Masked email" value={booking.email || booking.masked_email} />
              <Info label="Masked phone" value={booking.phone || booking.masked_phone} />
            </div>
            {booking.square_payment_url && (
              <a href={booking.square_payment_url} className="mt-5 inline-flex rounded-lg bg-[#d7bb77] px-4 py-3 font-semibold text-[#171913] hover:bg-[#e5cc90]">
                Open payment link
              </a>
            )}
            {booking.square_receipt_url && (
              <a href={booking.square_receipt_url} className="mt-5 inline-flex rounded-lg border border-[#d7bb77]/50 px-4 py-3 font-semibold text-[#f6f2e9]">
                Open receipt
              </a>
            )}
          </section>
        ))}
      </main>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="min-w-0 py-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#aaa99e]">{label}</p>
      <p className="mt-1 break-words font-semibold text-[#f6f2e9]">{typeof value === "string" || typeof value === "number" ? value || "-" : "-"}</p>
    </div>
  );
}
